import { useState, useCallback } from 'react'
import {
  Typography,
  IconButton,
  Button,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import truncate from 'lodash/truncate'
import { PiCheckCircleDuotone, PiPlusBold } from 'react-icons/pi'
import { addAgamiToPattern } from '../utils/helpers/supabase.helpers'
import { useGeneralToast } from '../hooks/toast'
import ResponsiveDialog from '../components/ResponsiveDialog'
import { formatDateString } from '../utils/helpers/dates.helpers'

interface ExperienceContentSpot {
  experience_id: string
  manual_code?: string
  pattern_id?: string
}

interface AddAgamiToPatternRowProps {
  id: string
  name: string
  description: string
  inserted_at: string
  experience_content_spots: ExperienceContentSpot[]
  refetch: () => Promise<void>
  currentPatternId: string
  currentExpId: string
}

const AddAgamiToPatternRow: React.FC<AddAgamiToPatternRowProps> = ({
  id,
  name,
  description,
  inserted_at,
  experience_content_spots,
  refetch,
  currentPatternId,
  currentExpId,
}) => {
  const { t } = useTranslation()
  const { error: toastError } = useGeneralToast()
  const [adding, setAdding] = useState<string | undefined>()
  const agamiExpRelationIds = experience_content_spots?.map((expRel) => expRel.pattern_id) ?? []

  const cancelAddAgamiToPattern = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setAdding(undefined)
  }, [])

  const confirmAddAgamiToPattern = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setAdding('loading')

      const { error } = await addAgamiToPattern({
        agamiId: id,
        patternId: currentPatternId,
        experienceId: currentExpId,
      })

      if (error) {
        toastError(error?.message || 'An error occurred')
        setAdding(undefined)
        return
      }

      await refetch()
      setAdding(undefined)
    },
    [id, agamiExpRelationIds, currentPatternId, currentExpId]
  )

  const handleAddAgamiToPattern = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (
        experience_content_spots[0]?.pattern_id &&
        experience_content_spots[0].pattern_id !== currentPatternId
      ) {
        setAdding('awaitingConfirmation')
      } else {
        confirmAddAgamiToPattern(e)
      }
    },
    [experience_content_spots, currentPatternId, confirmAddAgamiToPattern]
  )

  const alreadyPresent = agamiExpRelationIds?.includes(currentPatternId)

  return (
    <>
      <ResponsiveDialog
        isOpen={adding === 'awaitingConfirmation'}
        setIsOpen={(isOpen) => !isOpen && setAdding(undefined)}
        title={t('pattern.alreadyInPattern')}
      >
        <DialogHeader>{t('pattern.alreadyInPattern')}</DialogHeader>

        <DialogBody>
          <span className="block my-2">{t('pattern.confirmMessage')}</span>
          <span />
        </DialogBody>
        <DialogFooter className="justify-between gap-2">
          <Button size="sm" variant="text" onClick={cancelAddAgamiToPattern}>
            {t('pattern.cancel')}
          </Button>
          <Button size="sm" onClick={confirmAddAgamiToPattern}>
            {t('pattern.confirm')}
          </Button>
        </DialogFooter>
      </ResponsiveDialog>

      <tr
        key={id}
        className="even:bg-blue-gray-50/50 dark:even:bg-primary-500 text-center"
        data-testclass="add-agami-to-pattern-row"
      >
        <td className="py-2">
          <Typography title={name} variant="small" className="font-black">
            {truncate(name, {
              separator: ' ',
            })}
          </Typography>
        </td>
        <td className="py-2">
          <Typography title={description} variant="small">
            {truncate(description, {
              separator: ' ',
            })}
          </Typography>
        </td>
        <td className="py-2">
          <Typography variant="small">{formatDateString(inserted_at)}</Typography>
        </td>
        <td className="py-2">
          {alreadyPresent ? (
            <span>
              <PiCheckCircleDuotone className="mx-auto text-green-800 dark:text-green-500 text-[20px] my-1" />
            </span>
          ) : (
            <IconButton
              variant="text"
              size="sm"
              color="yellow"
              onClick={handleAddAgamiToPattern}
              disabled={!!adding}
              data-testid="add-agami-to-pattern-row-btn"
            >
              <PiPlusBold className="text-primary dark:text-base-gray-500 text-[14px]" />
            </IconButton>
          )}
        </td>
      </tr>
    </>
  )
}

export default AddAgamiToPatternRow
